import { useEffect, useState } from 'react';
import axios from 'axios';

// Three-step merge wizard:
//   1. CHOOSE   — pick which selected customer becomes the parent, OR
//                 create a brand-new parent record. Validates inputs and
//                 then fetches a preview from the server.
//   2. PREVIEW  — server returns the new site-row fields that will be
//                 created for each child, plus per-child counts of
//                 related rows (notes / sub-contacts / docs / sites /
//                 credentials) and any warnings. User confirms.
//   3. RESULT   — after a successful execute, shows what was created
//                 and offers a "Open Parent" navigation hook.

function fullName(c) {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
}

function customerLabel(c) {
  const company = c.company || fullName(c) || `Customer #${c.id}`;
  const loc = [c.city, c.province_state].filter(Boolean).join(', ');
  return loc ? `${company} — ${loc}` : company;
}

export default function MergeToParentDialog({ open, selectedCustomers, onClose, onMerged }) {
  // step: 'choose' | 'preview' | 'result'
  const [step, setStep] = useState('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState([]);

  // Choose-step state.
  const [parentMode, setParentMode] = useState('existing'); // 'existing' | 'new'
  const [parentIdSelection, setParentIdSelection] = useState(null);
  const [newParent, setNewParent] = useState({ company: '', city: '', province_state: '' });

  // Preview + result state.
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setBusy(false);
    setError(null);
    setErrors([]);
    setPreviewData(null);
    setResult(null);
    setParentMode('existing');
    setParentIdSelection(selectedCustomers?.[0]?.id || null);
    setNewParent({ company: '', city: '', province_state: '' });
  }, [open, selectedCustomers]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  // Build the body the server expects. In 'existing' mode the chosen
  // parent is removed from child_ids; in 'new' mode every selected
  // customer becomes a child of the freshly-created parent.
  function buildBody() {
    if (parentMode === 'existing') {
      return {
        parent_id: parentIdSelection,
        child_ids: selectedCustomers
          .filter((c) => c.id !== parentIdSelection)
          .map((c) => c.id)
      };
    }
    return {
      new_parent: {
        company:        newParent.company.trim(),
        last_name:      newParent.company.trim(), // mirrors server default
        city:           newParent.city.trim()       || null,
        province_state: newParent.province_state.trim() || null
      },
      child_ids: selectedCustomers.map((c) => c.id)
    };
  }

  function validateChoose() {
    const e = [];
    if (parentMode === 'existing') {
      if (!parentIdSelection) e.push('Pick which selected customer becomes the parent.');
      if (selectedCustomers.length < 2) e.push('Select at least one more customer to merge.');
    } else {
      if (!newParent.company.trim()) e.push('Parent company name is required.');
      if (selectedCustomers.length === 0) e.push('Select at least one customer to merge under the new parent.');
    }
    return e;
  }

  async function handlePreview() {
    setError(null);
    setErrors([]);
    const local = validateChoose();
    if (local.length > 0) {
      setErrors(local);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post('/api/customers/merge-to-parent/preview', buildBody());
      setPreviewData(res.data);
      setStep('preview');
    } catch (err) {
      if (Array.isArray(err.response?.data?.errors)) {
        setErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleExecute() {
    setError(null);
    setErrors([]);
    setBusy(true);
    try {
      const res = await axios.post('/api/customers/merge-to-parent', buildBody());
      setResult(res.data);
      setStep('result');
      if (onMerged) onMerged(res.data);
    } catch (err) {
      if (Array.isArray(err.response?.data?.errors)) {
        setErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="modal-card modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Merge customers to a parent"
      >
        <header className="modal-head">
          <h3>
            {step === 'choose'  && `Merge ${selectedCustomers.length} Customer${selectedCustomers.length === 1 ? '' : 's'} to Parent`}
            {step === 'preview' && 'Preview Merge'}
            {step === 'result'  && 'Merge Complete'}
          </h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >×</button>
        </header>

        {/* ── Step 1 ── Choose parent ────────────────────────────────── */}
        {step === 'choose' && (
          <div className="modal-body">
            <p className="modal-sub">
              All selected records will be converted into <strong>Site</strong>
              {' '}records under one parent. The parent gets the child's notes,
              documents, sub-contacts, existing sites, and SFTP credentials.
              The child contact records are deleted after the migration.
            </p>

            <h4 className="preview-heading">Selected customers ({selectedCustomers.length})</h4>
            <ul className="merge-selected-list">
              {selectedCustomers.map((c) => (
                <li key={c.id}>
                  <span className="merge-selected-company">{c.company || fullName(c) || `#${c.id}`}</span>
                  {(c.city || c.province_state) && (
                    <span className="merge-selected-loc">
                      {' '}— {[c.city, c.province_state].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {c.site_number && <span className="merge-selected-meta cell-mono"> · #{c.site_number}</span>}
                </li>
              ))}
            </ul>

            <h4 className="preview-heading">Parent record</h4>
            <div className="merge-parent-mode">
              <label className="merge-radio">
                <input
                  type="radio"
                  name="parentMode"
                  checked={parentMode === 'existing'}
                  onChange={() => setParentMode('existing')}
                />
                <span>Designate one of the selected customers as the parent</span>
              </label>
              {parentMode === 'existing' && (
                <select
                  className="merge-parent-select"
                  value={parentIdSelection || ''}
                  onChange={(e) => setParentIdSelection(Number(e.target.value))}
                >
                  <option value="" disabled>Choose parent…</option>
                  {selectedCustomers.map((c) => (
                    <option key={c.id} value={c.id}>{customerLabel(c)}</option>
                  ))}
                </select>
              )}

              <label className="merge-radio">
                <input
                  type="radio"
                  name="parentMode"
                  checked={parentMode === 'new'}
                  onChange={() => setParentMode('new')}
                />
                <span>Create a new parent record (e.g. "Centex")</span>
              </label>
              {parentMode === 'new' && (
                <div className="merge-new-parent">
                  <label className="form-field">
                    <span>Company name <span className="req">*</span></span>
                    <input
                      type="text"
                      value={newParent.company}
                      onChange={(e) => setNewParent({ ...newParent, company: e.target.value })}
                      placeholder="Centex"
                    />
                  </label>
                  <label className="form-field">
                    <span>City</span>
                    <input
                      type="text"
                      value={newParent.city}
                      onChange={(e) => setNewParent({ ...newParent, city: e.target.value })}
                    />
                  </label>
                  <label className="form-field">
                    <span>Province / State</span>
                    <input
                      type="text"
                      value={newParent.province_state}
                      onChange={(e) => setNewParent({ ...newParent, province_state: e.target.value })}
                    />
                  </label>
                </div>
              )}
            </div>

            {errors.length > 0 && (
              <ul className="error" style={{ marginTop: 'var(--space-3)' }}>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            {error && <p className="error">{error}</p>}

            <div className="form-actions" style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handlePreview} disabled={busy}>
                {busy ? 'Loading…' : 'Preview Merge →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2 ── Preview ──────────────────────────────────────── */}
        {step === 'preview' && previewData && (
          <div className="modal-body">
            <div className="quote-step-card">
              <div className="quote-step-icon quote-step-icon-info">⚠️</div>
              <div>
                <h4>About to merge {previewData.children.length} record{previewData.children.length === 1 ? '' : 's'}</h4>
                <p>
                  Parent: <strong>{previewData.parent.company || previewData.parent.last_name}</strong>
                  {previewData.parent.existing
                    ? <> (existing customer #{previewData.parent.id})</>
                    : <> (will be created)</>}.
                  This action <strong>cannot be undone</strong> — child customer
                  records will be deleted after the migration.
                </p>
              </div>
            </div>

            <h4 className="preview-heading">Sites that will be created</h4>
            <div className="merge-preview-list">
              {previewData.children.map((child) => (
                <div key={child.customer_id} className="merge-preview-row">
                  <div className="merge-preview-head">
                    <strong>{child.company || `Customer #${child.customer_id}`}</strong>
                    {child.contact_name && <span className="cell-muted"> · {child.contact_name}</span>}
                  </div>

                  {child.error && <p className="error">{child.error}</p>}

                  {child.site_fields && (
                    <dl className="merge-site-fields">
                      <div><dt>Site #</dt><dd className="cell-mono">{child.site_fields.site_number || '—'}</dd></div>
                      <div><dt>Site Name</dt><dd>{child.site_fields.site_name || '—'}</dd></div>
                      <div><dt>Address</dt><dd>{child.site_fields.address || '—'}</dd></div>
                      <div><dt>City</dt><dd>{child.site_fields.city || '—'}</dd></div>
                      <div><dt>Province</dt><dd>{child.site_fields.province_state || '—'}</dd></div>
                      <div><dt>Postal</dt><dd className="cell-mono">{child.site_fields.postal_zip || '—'}</dd></div>
                      <div><dt>Fuel Brand</dt><dd>{child.site_fields.fuel_brand || '—'}</dd></div>
                      <div><dt>POS Type</dt><dd>{child.site_fields.pos_type || '—'}</dd></div>
                      <div><dt>Install Date</dt><dd className="cell-mono">{child.site_fields.install_date || '—'}</dd></div>
                      <div><dt>Renewal Date</dt><dd className="cell-mono">{child.site_fields.renewal_date || '—'}</dd></div>
                      <div><dt>Phone</dt><dd className="cell-mono">{child.site_fields.site_phone || '—'}</dd></div>
                      <div><dt>Manager</dt><dd>{child.site_fields.site_manager || '—'}</dd></div>
                      <div><dt>XSite IO User</dt><dd className="cell-mono">{child.site_fields.xsite_io_username || '—'}</dd></div>
                    </dl>
                  )}

                  {child.counts && (
                    <div className="merge-counts">
                      <span><strong>{child.counts.notes}</strong> notes</span>
                      <span><strong>{child.counts.sub_contacts}</strong> sub-contacts</span>
                      <span><strong>{child.counts.documents}</strong> docs</span>
                      <span><strong>{child.counts.existing_sites}</strong> existing sites</span>
                      <span><strong>{child.counts.credentials}</strong> credentials</span>
                      <span>
                        Orphan subscription:{' '}
                        <strong>{child.counts.orphan_subscription ? 'yes' : 'no'}</strong>
                      </span>
                    </div>
                  )}

                  {child.warnings && child.warnings.length > 0 && (
                    <ul className="merge-warnings">
                      {child.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {errors.length > 0 && (
              <ul className="error" style={{ marginTop: 'var(--space-3)' }}>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            {error && <p className="error">{error}</p>}

            <div className="form-actions" style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn-secondary" onClick={() => setStep('choose')} disabled={busy}>
                ← Back
              </button>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleExecute} disabled={busy}>
                {busy ? 'Merging…' : `Confirm & Merge ${previewData.children.length} record${previewData.children.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3 ── Result ───────────────────────────────────────── */}
        {step === 'result' && result && (
          <div className="modal-body">
            <div className="quote-success">
              <div className="quote-success-icon">✓</div>
              <h4>Merge complete</h4>
              <p>
                Created <strong>{result.sites_created}</strong> new site
                record{result.sites_created === 1 ? '' : 's'} under parent
                {' '}#<strong>{result.parent_id}</strong>
                {result.parent_created && <> (new record)</>}.
              </p>
            </div>

            <h4 className="preview-heading">Per-record summary</h4>
            <table className="data-table merge-result-table">
              <thead>
                <tr>
                  <th>From customer</th>
                  <th>New site</th>
                  <th>Notes</th>
                  <th>Sub-contacts</th>
                  <th>Docs</th>
                  <th>Sites moved</th>
                  <th>Legacy creds</th>
                </tr>
              </thead>
              <tbody>
                {result.details.map((d) => (
                  <tr key={d.old_customer_id}>
                    <td>{d.company || `#${d.old_customer_id}`}</td>
                    <td className="cell-mono">#{d.new_site_id}</td>
                    <td>{d.moved_notes}</td>
                    <td>{d.moved_sub_contacts}</td>
                    <td>{d.moved_documents}</td>
                    <td>{d.moved_existing_sites}</td>
                    <td>{d.legacy_credentials_preserved ? 'preserved as note' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="form-actions" style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
